import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Calculator, DollarSign, TrendingUp, TrendingDown, FileText, 
  Plus, RefreshCw, Download, Trash2, Edit2, Receipt, Pencil, Eye,
  PieChart, BarChart3, FileSpreadsheet, ArrowRight, ArrowLeft,
  Building2, CreditCard, Wallet, Send, CheckCircle, Clock, AlertCircle, Upload,
  Users, FileCheck, Printer, Link2, Unlink, Zap, Landmark, ExternalLink
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line } from 'recharts';
import axios from 'axios';

// Extracted Components
import PaymentModal from '@/components/accounting/PaymentModal';
import AgingReportModal from '@/components/accounting/AgingReportModal';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Accounting() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [balancesRecalculated, setBalancesRecalculated] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [t4aAgents, setT4aAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]); // All agents for invoice client dropdown
  const [t4aSummary, setT4aSummary] = useState(null);
  const [taxSlipsData, setTaxSlipsData] = useState({ employees: [], contractors: [], summary: {} });
  const [bankTransactions, setBankTransactions] = useState([]);
  const [reconciliationSummary, setReconciliationSummary] = useState(null);
  
  // Modal states
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showChartAccountModal, setShowChartAccountModal] = useState(false);
  const [showIncomeDetailsModal, setShowIncomeDetailsModal] = useState(false);
  const [showExpenseDetailsModal, setShowExpenseDetailsModal] = useState(false);
  const [showAccountTransactionsModal, setShowAccountTransactionsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAgingReport, setShowAgingReport] = useState(false);
  const [incomeDetails, setIncomeDetails] = useState(null);
  const [expenseDetails, setExpenseDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [agingReportData, setAgingReportData] = useState(null);
  
  // Transaction management states
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingChartAccount, setEditingChartAccount] = useState(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // single transaction or 'bulk'
  
  // Form states
  const [transactionForm, setTransactionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    account_id: '',
    debit: '',
    credit: '',
    category: '',
    reference: '',
    vendor: ''
  });
  
  const [chartAccountForm, setChartAccountForm] = useState({
    name: '',
    account_type: 'expense',
    account_number: '',
    description: '',
    parent_account_id: '',
    is_active: true
  });
  
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: '',
    client_email: '',
    client_address: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    tax_rate: 0,
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    accounting_account_id: '',
    agent_id: '',
    status: 'draft',
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_end_date: ''
  });
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    reference: '',
    notes: ''
  });
  
  // Invoice filters
  const [invoiceFilters, setInvoiceFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    agentId: ''
  });
  const [outstandingInvoices, setOutstandingInvoices] = useState(null);

  useEffect(() => {
    loadData();
  }, [fiscalYear]);

  const loadData = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      const [
        dashboardRes,
        transactionsRes,
        accountsRes,
        invoicesRes,
        fiscalYearsRes,
        profitLossRes,
        balanceSheetRes,
        taxSummaryRes,
        t4aAgentsRes,
        t4aSummaryRes,
        bankTransRes,
        reconcileSummaryRes,
        allAgentsRes
      ] = await Promise.all([
        axios.get(`${API}/api/accounting/dashboard?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/transactions?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/chart-of-accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/fiscal-years`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/profit-loss?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/balance-sheet`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reports/tax-summary?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/tax-slips/agents?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/t4a/summary?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/bank-transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/reconciliation/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/agents`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDashboard(dashboardRes.data);
      setTransactions(transactionsRes.data);
      setChartOfAccounts(accountsRes.data);
      setInvoices(invoicesRes.data);
      setFiscalYears(fiscalYearsRes.data.fiscal_years);
      setProfitLoss(profitLossRes.data);
      setBalanceSheet(balanceSheetRes.data);
      setTaxSummary(taxSummaryRes.data);
      setT4aAgents(t4aAgentsRes.data.agents || [...(t4aAgentsRes.data.employees || []), ...(t4aAgentsRes.data.contractors || [])]);
      setTaxSlipsData(t4aAgentsRes.data);
      setT4aSummary(t4aSummaryRes.data);
      setBankTransactions(bankTransRes.data || []);
      setReconciliationSummary(reconcileSummaryRes.data);
      setAllAgents(allAgentsRes.data || []);
    } catch (err) {
      toast.error('Failed to load accounting data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeDetails = async () => {
    setDetailsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/api/accounting/dashboard/income-details?fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIncomeDetails(response.data);
      setShowIncomeDetailsModal(true);
    } catch (err) {
      toast.error('Failed to load income details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchExpenseDetails = async () => {
    setDetailsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/api/accounting/dashboard/expense-details?fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExpenseDetails(response.data);
      setShowExpenseDetailsModal(true);
    } catch (err) {
      toast.error('Failed to load expense details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchNetProfitDetails = async () => {
    // Fetch both income and expense details
    setDetailsLoading(true);
    try {
      const token = getToken();
      const [incomeRes, expenseRes] = await Promise.all([
        axios.get(`${API}/api/accounting/dashboard/income-details?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/dashboard/expense-details?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setIncomeDetails(incomeRes.data);
      setExpenseDetails(expenseRes.data);
      // Show income modal first, user can switch to expenses
      setShowIncomeDetailsModal(true);
    } catch (err) {
      toast.error('Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Navigation functions for detail modals
  const navigateToCommission = (commission) => {
    // Close the modal and navigate to commissions page
    setShowIncomeDetailsModal(false);
    setShowExpenseDetailsModal(false);
    
    // Set the business unit based on division
    if (commission.division === 'insurance') {
      localStorage.setItem('businessUnit', 'insurance');
    } else {
      localStorage.setItem('businessUnit', 'mortgage');
    }
    
    // Navigate to commissions page with search query
    const searchTerm = commission.description || commission.agent_name || '';
    navigate(`/commissions?search=${encodeURIComponent(searchTerm)}`);
  };

  const navigateToExpense = (expense) => {
    setShowIncomeDetailsModal(false);
    setShowExpenseDetailsModal(false);
    
    if (expense.division === 'insurance') {
      localStorage.setItem('businessUnit', 'insurance');
    } else {
      localStorage.setItem('businessUnit', 'mortgage');
    }
    
    navigate('/expenses');
  };

  const navigateToTransaction = (transaction) => {
    setShowIncomeDetailsModal(false);
    setShowExpenseDetailsModal(false);
    
    // Switch to transactions tab on current page
    setActiveTab('transactions');
  };

  const syncFromDivisions = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/sync-from-divisions?fiscal_year=${fiscalYear}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to sync data');
    }
  };

  const createTransaction = async () => {
    if (!transactionForm.account_id || !transactionForm.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/transactions`, {
        ...transactionForm,
        debit: parseFloat(transactionForm.debit) || 0,
        credit: parseFloat(transactionForm.credit) || 0,
        source: 'manual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Transaction created');
      setShowTransactionModal(false);
      resetTransactionForm();
      loadData();
    } catch (err) {
      toast.error('Failed to create transaction');
    }
  };

  const updateTransaction = async () => {
    if (!transactionForm.account_id || !transactionForm.description) {
      toast.error('Please fill in required fields');
      return;
    }
    
    const token = getToken();
    try {
      await axios.put(`${API}/api/accounting/transactions/${editingTransactionId}`, {
        ...transactionForm,
        debit: parseFloat(transactionForm.debit) || 0,
        credit: parseFloat(transactionForm.credit) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Transaction updated');
      setShowTransactionModal(false);
      setEditingTransactionId(null);
      resetTransactionForm();
      loadData();
    } catch (err) {
      toast.error('Failed to update transaction');
    }
  };

  const deleteTransaction = async (transactionId) => {
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete transaction');
    }
  };

  const bulkDeleteTransactions = async () => {
    if (selectedTransactionIds.size === 0) return;
    
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/transactions/bulk-delete`, {
        ids: Array.from(selectedTransactionIds)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Deleted ${selectedTransactionIds.size} transaction(s)`);
      setSelectedTransactionIds(new Set());
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget === 'bulk') {
      bulkDeleteTransactions();
    } else if (deleteTarget) {
      deleteTransaction(deleteTarget);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      date: new Date().toISOString().split('T')[0],
      description: '',
      account_id: '',
      debit: '',
      credit: '',
      category: '',
      reference: '',
      vendor: ''
    });
  };

  const openEditTransaction = (transaction) => {
    setEditingTransactionId(transaction.id);
    setTransactionForm({
      date: transaction.date || new Date().toISOString().split('T')[0],
      description: transaction.description || '',
      account_id: transaction.account_id || '',
      debit: transaction.debit?.toString() || '',
      credit: transaction.credit?.toString() || '',
      category: transaction.category || '',
      reference: transaction.reference || '',
      vendor: transaction.vendor || ''
    });
    setShowTransactionModal(true);
  };

  const toggleTransactionSelection = (id) => {
    setSelectedTransactionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleAllTransactions = () => {
    if (selectedTransactionIds.size === transactions.length) {
      setSelectedTransactionIds(new Set());
    } else {
      setSelectedTransactionIds(new Set(transactions.map(t => t.id)));
    }
  };

  const createInvoice = async () => {
    if (!invoiceForm.client_name || invoiceForm.items.length === 0) {
      toast.error('Please fill in client name and add at least one item');
      return;
    }
    
    const token = getToken();
    try {
      const items = invoiceForm.items.map(item => ({
        ...item,
        amount: item.quantity * item.unit_price
      }));
      
      if (editingInvoice) {
        // Update existing invoice
        await axios.put(`${API}/api/accounting/invoices/${editingInvoice.id}`, {
          ...invoiceForm,
          items
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Invoice updated');
      } else {
        // Create new invoice
        await axios.post(`${API}/api/accounting/invoices`, {
          ...invoiceForm,
          items
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Invoice created');
      }
      
      setShowInvoiceModal(false);
      setEditingInvoice(null);
      resetInvoiceForm();
      loadData();
    } catch (err) {
      toast.error(editingInvoice ? 'Failed to update invoice' : 'Failed to create invoice');
    }
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({
      client_name: '',
      client_email: '',
      client_address: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      tax_rate: 0,
      notes: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      accounting_account_id: '',
      agent_id: '',
      status: 'draft',
      is_recurring: false,
      recurring_frequency: 'monthly',
      recurring_end_date: ''
    });
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      client_name: invoice.client_name,
      client_email: invoice.client_email || '',
      client_address: invoice.client_address || '',
      invoice_date: invoice.invoice_date,
      due_date: invoice.due_date,
      tax_rate: invoice.tax_rate,
      notes: invoice.notes || '',
      items: invoice.items.length > 0 ? invoice.items : [{ description: '', quantity: 1, unit_price: 0 }],
      accounting_account_id: invoice.accounting_account_id || '',
      agent_id: invoice.agent_id || '',
      status: invoice.status,
      is_recurring: invoice.is_recurring || false,
      recurring_frequency: invoice.recurring_frequency || 'monthly',
      recurring_end_date: invoice.recurring_end_date || ''
    });
    setShowInvoiceModal(true);
  };

  const handleAccountClick = async (account) => {
    setSelectedAccount(account);
    setDetailsLoading(true);
    const token = getToken();
    
    try {
      const response = await axios.get(
        `${API}/api/accounting/transactions?account_id=${account.id}&fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccountTransactions(response.data);
      setShowAccountTransactionsModal(true);
    } catch (err) {
      toast.error('Failed to load account transactions');
    } finally {
      setDetailsLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    const token = getToken();
    try {
      await axios.put(
        `${API}/api/accounting/invoices/${invoiceId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invoice marked as ${status}`);
      loadData();
    } catch (err) {
      toast.error('Failed to update invoice');
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleAgentSelectForInvoice = (agentId) => {
    if (!agentId || agentId === 'manual') {
      // Clear to allow manual entry
      return;
    }
    const agent = allAgents.find(a => a.id === agentId);
    if (agent) {
      setInvoiceForm({
        ...invoiceForm,
        client_name: agent.name || '',
        client_email: agent.email || '',
        client_address: agent.address || '',
        agent_id: agentId
      });
      toast.success(`Client info populated from ${agent.name}`);
    }
  };

  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const removeInvoiceItem = (index) => {
    const newItems = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm({ ...invoiceForm, items: newItems.length > 0 ? newItems : [{ description: '', quantity: 1, unit_price: 0 }] });
  };

  const calculateInvoiceTotal = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * (invoiceForm.tax_rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  // Invoice PDF and Email functions
  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/accounting/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const emailInvoice = async (invoiceId) => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/${invoiceId}/email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
  };

  const loadOutstandingInvoices = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(`${API}/api/accounting/invoices/outstanding?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutstandingInvoices(response.data);
    } catch (err) {
      toast.error('Failed to load outstanding invoices');
    }
  };

  const downloadOutstandingStatement = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(`${API}/api/accounting/invoices/statement/outstanding?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Outstanding_Statement_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Outstanding statement downloaded');
    } catch (err) {
      toast.error('Failed to download statement');
    }
  };

  const emailOutstandingStatement = async (agentId) => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/invoices/statement/email?agent_id=${agentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send statement');
    }
  };

  const filterInvoices = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.startDate) params.append('start_date', invoiceFilters.startDate);
      if (invoiceFilters.endDate) params.append('end_date', invoiceFilters.endDate);
      if (invoiceFilters.status) params.append('status', invoiceFilters.status);
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      
      const response = await axios.get(`${API}/api/accounting/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (err) {
      toast.error('Failed to filter invoices');
    }
  };

  // Payment Functions
  const openPaymentModal = (invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setPaymentForm({
      amount: invoice.balance_due || invoice.total,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'check',
      reference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const recordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/invoices/${selectedInvoiceForPayment.id}/payments`,
        {
          amount: parseFloat(paymentForm.amount),
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method,
          reference: paymentForm.reference,
          notes: paymentForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowPaymentModal(false);
      setSelectedInvoiceForPayment(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const deletePayment = async (invoiceId, paymentId) => {
    if (!confirm('Are you sure you want to delete this payment?')) return;
    
    const token = getToken();
    try {
      const response = await axios.delete(
        `${API}/api/accounting/invoices/${invoiceId}/payments/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to delete payment');
    }
  };

  // Aging Report Functions
  const loadAgingReport = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(
        `${API}/api/accounting/invoices/aging-report?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgingReportData(response.data);
      setShowAgingReport(true);
    } catch (err) {
      toast.error('Failed to load aging report');
    }
  };

  const downloadAgingReportPdf = async () => {
    const token = getToken();
    try {
      const params = new URLSearchParams();
      if (invoiceFilters.agentId) params.append('agent_id', invoiceFilters.agentId);
      const response = await axios.get(
        `${API}/api/accounting/invoices/aging-report/pdf?${params}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Aging_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Aging report downloaded');
    } catch (err) {
      toast.error('Failed to download aging report');
    }
  };

  // Recurring Invoice Functions
  const generateRecurringInvoices = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/invoices/generate-recurring`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to generate recurring invoices');
    }
  };

  // Update Overdue Invoices
  const updateOverdueInvoices = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/invoices/update-overdue`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to update overdue invoices');
    }
  };

  // Recalculate Account Balances
  const recalculateBalances = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/chart-of-accounts/recalculate-balances`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      if (response.data.changes && response.data.changes.length > 0) {
        console.log('Balance changes:', response.data.changes);
      }
      loadData();
    } catch (err) {
      toast.error('Failed to recalculate balances');
    }
  };

  // Silent auto-recalculate (runs once per session when switching to Accounts tab)
  const autoRecalculateBalances = async () => {
    if (balancesRecalculated) return;
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/chart-of-accounts/recalculate-balances`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBalancesRecalculated(true);
      if (response.data.updated_count > 0) {
        toast.info(`Synced ${response.data.updated_count} account balance(s)`);
        loadData();
      }
    } catch (err) {
      console.error('Auto-recalculate failed:', err);
    }
  };

  // Handle tab changes with auto-recalculate for Accounts tab
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'accounts' && isAdmin) {
      autoRecalculateBalances();
    }
  };

  // Send invoice due date reminders
  const sendInvoiceReminders = async (daysBefore = 7) => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/invoices/send-reminders?days_before=${daysBefore}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} reminder(s) failed to send`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    }
  };

  // Chart of Accounts CRUD Functions
  const resetChartAccountForm = () => {
    setChartAccountForm({
      name: '',
      account_type: 'expense',
      account_number: '',
      description: '',
      parent_account_id: '',
      is_active: true
    });
    setEditingChartAccount(null);
  };

  const handleEditChartAccount = (account) => {
    setEditingChartAccount(account);
    setChartAccountForm({
      name: account.name,
      account_type: account.account_type,
      account_number: account.account_number,
      description: account.description || '',
      parent_account_id: account.parent_account_id || '',
      is_active: account.is_active !== false
    });
    setShowChartAccountModal(true);
  };

  const handleCreateSubAccount = (parentAccount) => {
    resetChartAccountForm();
    // Pre-fill with parent info
    const nextNumber = `${parentAccount.account_number}.1`;
    setChartAccountForm({
      name: '',
      account_type: parentAccount.account_type,
      account_number: nextNumber,
      description: '',
      parent_account_id: parentAccount.id,
      is_active: true
    });
    setShowChartAccountModal(true);
  };

  const saveChartAccount = async () => {
    if (!chartAccountForm.name || !chartAccountForm.account_number) {
      toast.error('Account name and number are required');
      return;
    }

    const token = getToken();
    try {
      if (editingChartAccount) {
        await axios.put(
          `${API}/api/accounting/chart-of-accounts/${editingChartAccount.id}`,
          chartAccountForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Account updated successfully');
      } else {
        await axios.post(
          `${API}/api/accounting/chart-of-accounts`,
          chartAccountForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Account created successfully');
      }
      setShowChartAccountModal(false);
      resetChartAccountForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save account');
    }
  };

  const deleteChartAccount = async (account) => {
    if (!confirm(`Are you sure you want to delete "${account.name}" (${account.account_number})? This action cannot be undone.`)) {
      return;
    }

    const token = getToken();
    try {
      await axios.delete(
        `${API}/api/accounting/chart-of-accounts/${account.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Account "${account.name}" deleted`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  // T4A Functions
  const downloadT4aSlip = async (agentId, agentName) => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/t4a/generate/${agentId}?fiscal_year=${fiscalYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4A_${agentName.replace(/\s+/g, '_')}_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`T4A slip downloaded for ${agentName}`);
    } catch (err) {
      toast.error('Failed to download T4A slip');
    }
  };

  // T4 Functions (for employees)
  const downloadT4Slip = async (agentId, agentName) => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/t4/generate/${agentId}?fiscal_year=${fiscalYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4_${agentName.replace(/\s+/g, '_')}_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`T4 slip downloaded for ${agentName}`);
    } catch (err) {
      toast.error('Failed to download T4 slip');
    }
  };

  // Generic PDF report download function
  const downloadPdfReport = async (reportId, filename) => {
    const token = getToken();
    try {
      // Build URL - if reportId contains query params, use it directly
      const url = reportId.includes('?') 
        ? `${API}/api/accounting/reports/pdf/${reportId}`
        : `${API}/api/accounting/reports/pdf/${reportId}?fiscal_year=${fiscalYear}`;
      
      const response = await axios.get(url, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlObj;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlObj);
      toast.success(`Downloaded: ${filename}`);
    } catch (err) {
      toast.error(`Failed to download ${filename}`);
      console.error(err);
    }
  };

  const downloadT4aSummary = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/t4a/summary/pdf?fiscal_year=${fiscalYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4A_Summary_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('T4A Summary downloaded');
    } catch (err) {
      toast.error('Failed to download T4A Summary');
    }
  };

  const downloadAllT4aSlips = async () => {
    toast.info('Downloading all T4A slips...');
    for (const agent of t4aAgents) {
      await downloadT4aSlip(agent.id, agent.name);
      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    toast.success(`Downloaded ${t4aAgents.length} T4A slips`);
  };

  // Bank Reconciliation Functions
  const [showBankImportModal, setShowBankImportModal] = useState(false);
  const [bankImportData, setBankImportData] = useState('');
  
  const autoMatchTransactions = async () => {
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/accounting/bank-transactions/auto-match`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Auto-match failed');
    }
  };

  const matchTransaction = async (bankTransId, accountingTransId) => {
    const token = getToken();
    try {
      await axios.post(
        `${API}/api/accounting/bank-transactions/${bankTransId}/match/${accountingTransId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transactions matched');
      loadData();
    } catch (err) {
      toast.error('Match failed');
    }
  };

  const unmatchTransaction = async (bankTransId) => {
    const token = getToken();
    try {
      await axios.post(
        `${API}/api/accounting/bank-transactions/${bankTransId}/unmatch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Transaction unmatched');
      loadData();
    } catch (err) {
      toast.error('Unmatch failed');
    }
  };

  const deleteBankTransaction = async (id) => {
    if (!window.confirm('Delete this bank transaction?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/bank-transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const importBankTransactions = async () => {
    if (!bankImportData.trim()) {
      toast.error('Please enter bank transaction data');
      return;
    }
    
    const token = getToken();
    try {
      // Parse CSV-like data (Date, Description, Amount)
      const lines = bankImportData.trim().split('\n');
      const transactions = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        const amount = parseFloat(parts[2] || '0');
        return {
          date: parts[0] || new Date().toISOString().split('T')[0],
          description: parts[1] || 'Bank Transaction',
          amount: Math.abs(amount),
          transaction_type: amount >= 0 ? 'credit' : 'debit',
          bank_account: 'main'
        };
      }).filter(t => t.description);
      
      await axios.post(
        `${API}/api/accounting/bank-transactions/import`,
        transactions,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Imported ${transactions.length} transactions`);
      setShowBankImportModal(false);
      setBankImportData('');
      loadData();
    } catch (err) {
      toast.error('Import failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Calculator className="w-8 h-8" />
            Accounting
          </h1>
          <p className="text-slate-500 mt-1">Full accounting module with income, expenses, and reports</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={fiscalYear.toString()} onValueChange={(v) => setFiscalYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="fiscal-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fiscalYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={syncFromDivisions} data-testid="sync-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Data
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="t4a">T4/T4A</TabsTrigger>
          <TabsTrigger value="reconciliation">Bank Recon</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {/* Summary Cards */}
          {dashboard && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card 
                  className="cursor-pointer hover:shadow-lg hover:border-emerald-300 transition-all"
                  onClick={fetchIncomeDetails}
                  data-testid="income-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Total Income</p>
                        <p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency(dashboard.summary.total_income)}</p>
                        <p className="text-xs text-emerald-600 mt-1">Click for details →</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="cursor-pointer hover:shadow-lg hover:border-red-300 transition-all"
                  onClick={fetchExpenseDetails}
                  data-testid="expense-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                        <TrendingDown className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Total Expenses</p>
                        <p className="text-xl font-bold font-mono text-red-600">{formatCurrency(dashboard.summary.total_expenses)}</p>
                        <p className="text-xs text-red-600 mt-1">Click for details →</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
                  onClick={fetchNetProfitDetails}
                  data-testid="net-profit-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Net Profit</p>
                        <p className={`text-xl font-bold font-mono ${dashboard.summary.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          {formatCurrency(dashboard.summary.net_profit)}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">Click for breakdown →</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Invoices</p>
                        <p className="text-xl font-bold font-mono">{dashboard.invoices.count}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Chart */}
              {dashboard.monthly_breakdown.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Monthly Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={dashboard.monthly_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="income" name="Income" fill="#10b981" />
                        <Bar dataKey="expenses" name="Expenses" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Invoice Summary */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(dashboard.invoices.paid)}</p>
                    <p className="text-sm text-emerald-600">Paid Invoices</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-amber-700">{formatCurrency(dashboard.invoices.pending)}</p>
                    <p className="text-sm text-amber-600">Pending</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(dashboard.invoices.overdue)}</p>
                    <p className="text-sm text-red-600">Overdue</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <Wallet className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(dashboard.invoices.total)}</p>
                    <p className="text-sm text-blue-600">Total Invoiced</p>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Transactions</CardTitle>
                <CardDescription>{transactions.length} records for {fiscalYear}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && selectedTransactionIds.size > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => { setDeleteTarget('bulk'); setShowDeleteConfirm(true); }}
                    data-testid="bulk-delete-transactions-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedTransactionIds.size})
                  </Button>
                )}
                {isAdmin && (
                  <Button onClick={() => { resetTransactionForm(); setEditingTransactionId(null); setShowTransactionModal(true); }} data-testid="add-transaction-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Transaction
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isAdmin && (
                        <TableHead className="w-10">
                          <input 
                            type="checkbox" 
                            checked={selectedTransactionIds.size === transactions.length && transactions.length > 0}
                            onChange={toggleAllTransactions}
                            className="rounded border-slate-300"
                          />
                        </TableHead>
                      )}
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead>Source</TableHead>
                      {isAdmin && <TableHead className="w-24">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 11 : 9} className="text-center py-8 text-slate-500">
                          No transactions. Click &quot;Sync Data&quot; to import from Mortgage and Insurance divisions.
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map(trans => (
                        <TableRow key={trans.id} className={selectedTransactionIds.has(trans.id) ? 'bg-blue-50' : ''}>
                          {isAdmin && (
                            <TableCell>
                              <input 
                                type="checkbox" 
                                checked={selectedTransactionIds.has(trans.id)}
                                onChange={() => toggleTransactionSelection(trans.id)}
                                className="rounded border-slate-300"
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-sm">{formatDate(trans.date)}</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={trans.description}>{trans.description}</TableCell>
                          <TableCell>{trans.account_name}</TableCell>
                          <TableCell className="max-w-[120px] truncate" title={trans.vendor || ''}>
                            {trans.vendor || <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell>
                            {trans.category && <Badge variant="outline">{trans.category}</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">
                            {trans.debit > 0 ? formatCurrency(trans.debit) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-600">
                            {trans.credit > 0 ? formatCurrency(trans.credit) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize text-xs">{trans.source}</Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => openEditTransaction(trans)}
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => { setDeleteTarget(trans.id); setShowDeleteConfirm(true); }}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6 space-y-4">
          {/* Invoice Filters */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium">Filter & Actions</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={invoiceFilters.startDate}
                    onChange={(e) => setInvoiceFilters({ ...invoiceFilters, startDate: e.target.value })}
                    className="w-36 h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={invoiceFilters.endDate}
                    onChange={(e) => setInvoiceFilters({ ...invoiceFilters, endDate: e.target.value })}
                    className="w-36 h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={invoiceFilters.status || "all"} onValueChange={(v) => setInvoiceFilters({ ...invoiceFilters, status: v === "all" ? "" : v })}>
                    <SelectTrigger className="w-32 h-9">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="partially_paid">Partially Paid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Agent</Label>
                  <Select value={invoiceFilters.agentId || "all"} onValueChange={(v) => setInvoiceFilters({ ...invoiceFilters, agentId: v === "all" ? "" : v })}>
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents</SelectItem>
                      {allAgents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={filterInvoices} data-testid="filter-invoices-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Filter
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setInvoiceFilters({ startDate: '', endDate: '', status: '', agentId: '' }); loadData(); }}>
                  Clear
                </Button>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={loadAgingReport} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="aging-report-btn">
                    <BarChart3 className="w-4 h-4 mr-1" /> Aging Report
                  </Button>
                  <Button size="sm" variant="outline" onClick={loadOutstandingInvoices} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    <AlertCircle className="w-4 h-4 mr-1" /> Outstanding
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadOutstandingStatement} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <Download className="w-4 h-4 mr-1" /> Statement PDF
                  </Button>
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => sendInvoiceReminders(7)} className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="send-reminders-btn" title="Send reminders to invoices due within 7 days">
                        <Send className="w-4 h-4 mr-1" /> Send Reminders
                      </Button>
                      <Button size="sm" variant="outline" onClick={updateOverdueInvoices} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="update-overdue-btn">
                        <Clock className="w-4 h-4 mr-1" /> Update Overdue
                      </Button>
                      <Button size="sm" variant="outline" onClick={generateRecurringInvoices} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="generate-recurring-btn">
                        <RefreshCw className="w-4 h-4 mr-1" /> Generate Recurring
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Outstanding Summary */}
              {outstandingInvoices && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-6">
                      <div>
                        <p className="text-xs text-amber-600">Outstanding Invoices</p>
                        <p className="text-lg font-bold text-amber-700">{outstandingInvoices.invoice_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-amber-600">Total Outstanding</p>
                        <p className="text-lg font-bold font-mono text-amber-700">{formatCurrency(outstandingInvoices.total_outstanding)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-red-600">Overdue</p>
                        <p className="text-lg font-bold text-red-700">{outstandingInvoices.overdue_count}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setOutstandingInvoices(null)}>
                      ✕
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Invoices</CardTitle>
                <CardDescription>Manage customer invoices - linked to transactions and agent expenses</CardDescription>
              </div>
              {isAdmin && (
                <Button onClick={() => { resetInvoiceForm(); setEditingInvoice(null); setShowInvoiceModal(true); }} data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Invoice
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      {isAdmin && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                          No invoices created yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map(inv => (
                        <TableRow key={inv.id} className={inv.status === 'overdue' || (inv.due_date < new Date().toISOString().split('T')[0] && inv.status !== 'paid' && inv.status !== 'cancelled') ? 'bg-red-50' : ''}>
                          <TableCell className="font-mono">
                            {inv.invoice_number}
                            {inv.is_recurring && <Badge variant="outline" className="ml-1 text-xs bg-blue-50">Recurring</Badge>}
                          </TableCell>
                          <TableCell>{inv.client_name}</TableCell>
                          <TableCell>
                            {inv.agent_name ? (
                              <Badge variant="outline" className="text-xs">{inv.agent_name}</Badge>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{formatDate(inv.invoice_date)}</TableCell>
                          <TableCell className="font-mono text-sm">{formatDate(inv.due_date)}</TableCell>
                          <TableCell>
                            {inv.accounting_account_name ? (
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {inv.accounting_account_name}
                              </Badge>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">{formatCurrency(inv.total)}</TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            {inv.status === 'paid' ? (
                              <span className="text-emerald-600">Paid</span>
                            ) : (
                              <span className={inv.balance_due > 0 ? 'text-amber-600' : ''}>
                                {formatCurrency(inv.balance_due || inv.total)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                              inv.status === 'partially_paid' ? 'bg-amber-100 text-amber-800' :
                              inv.status === 'overdue' ? 'bg-red-100 text-red-800' :
                              inv.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                              'bg-slate-100 text-slate-800'
                            }>
                              {inv.status === 'partially_paid' ? 'Partial' : inv.status}
                            </Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <div className="flex gap-1">
                                {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => openPaymentModal(inv)}
                                    title="Record Payment"
                                    data-testid={`record-payment-${inv.id}`}
                                  >
                                    <DollarSign className="w-4 h-4 text-emerald-500" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number)}
                                  title="Download PDF"
                                >
                                  <Download className="w-4 h-4 text-emerald-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => emailInvoice(inv.id)}
                                  title="Email to Client"
                                  disabled={!inv.client_email}
                                >
                                  <Send className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleEditInvoice(inv)}
                                  data-testid={`edit-invoice-${inv.id}`}
                                  title="Edit Invoice"
                                >
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                {inv.status !== 'paid' && (
                                  <Button variant="ghost" size="sm" onClick={() => updateInvoiceStatus(inv.id, 'paid')} title="Mark as Paid">
                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                  </Button>
                                )}
                                {inv.status === 'draft' && (
                                  <Button variant="ghost" size="sm" onClick={() => updateInvoiceStatus(inv.id, 'sent')} title="Mark as Sent">
                                    <Send className="w-4 h-4 text-blue-500" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => deleteInvoice(inv.id)}
                                  data-testid={`delete-invoice-${inv.id}`}
                                  title="Delete Invoice"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-6 space-y-6">
          {/* Profit & Loss */}
          {profitLoss && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Profit & Loss Statement
                </CardTitle>
                <CardDescription>Period: {profitLoss.period.start} to {profitLoss.period.end}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-emerald-700 mb-3">Income</h4>
                    {profitLoss.income.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-2 border-b">
                        <span>{item.category}</span>
                        <span className="font-mono">{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 font-bold text-emerald-700">
                      <span>Total Income</span>
                      <span className="font-mono">{formatCurrency(profitLoss.total_income)}</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-700 mb-3">Expenses</h4>
                    {profitLoss.expenses.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-2 border-b">
                        <span>{item.category}</span>
                        <span className="font-mono">{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 font-bold text-red-700">
                      <span>Total Expenses</span>
                      <span className="font-mono">{formatCurrency(profitLoss.total_expenses)}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-slate-100 rounded-lg flex justify-between items-center">
                  <span className="text-lg font-semibold">Net Profit</span>
                  <span className={`text-2xl font-bold font-mono ${profitLoss.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(profitLoss.net_profit)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tax Summary */}
          {taxSummary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Tax Summary ({fiscalYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm text-blue-600">Gross Income</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(taxSummary.gross_income)}</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-sm text-amber-600">Total Deductions</p>
                    <p className="text-2xl font-bold text-amber-700">{formatCurrency(taxSummary.total_deductions)}</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-sm text-emerald-600">Taxable Income</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(taxSummary.taxable_income)}</p>
                  </div>
                </div>
                
                <h4 className="font-semibold mb-3">Tax-Deductible Expense Breakdown</h4>
                <div className="space-y-2">
                  {taxSummary.expense_breakdown.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b">
                      <span className="flex items-center gap-2">
                        {item.category}
                        {item.tax_deductible && <Badge variant="outline" className="text-xs">Deductible</Badge>}
                      </span>
                      <span className="font-mono">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> {taxSummary.notes[0]}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          {/* PDF Reports Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Generate PDF Reports (Canadian Standard)
              </CardTitle>
              <CardDescription>
                Download official reports for accounting, tax filing, and compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Financial Statements */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Financial Statements
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport('income-statement', `Income_Statement_${fiscalYear}.pdf`)}
                      data-testid="download-income-statement"
                    >
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <span className="text-xs text-center">Income Statement<br/>(P&L)</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport('balance-sheet', `Balance_Sheet_${fiscalYear}.pdf`)}
                      data-testid="download-balance-sheet"
                    >
                      <Wallet className="w-5 h-5 text-blue-600" />
                      <span className="text-xs text-center">Balance Sheet</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport('cash-flow', `Cash_Flow_${fiscalYear}.pdf`)}
                      data-testid="download-cash-flow"
                    >
                      <DollarSign className="w-5 h-5 text-purple-600" />
                      <span className="text-xs text-center">Cash Flow<br/>Statement</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport('general-ledger', `General_Ledger_${fiscalYear}.pdf`)}
                      data-testid="download-general-ledger"
                    >
                      <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                      <span className="text-xs text-center">General<br/>Ledger</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport('trial-balance', `Trial_Balance_${fiscalYear}.pdf`)}
                      data-testid="download-trial-balance"
                    >
                      <Calculator className="w-5 h-5 text-slate-600" />
                      <span className="text-xs text-center">Trial<br/>Balance</span>
                    </Button>
                  </div>
                </div>

                {/* Corporate Tax Reports */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Corporate Tax Reports
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2 border-red-200 hover:bg-red-50"
                      onClick={() => downloadPdfReport('t2-corporate-tax', `T2_Corporate_Tax_${fiscalYear}.pdf`)}
                      data-testid="download-t2"
                    >
                      <Landmark className="w-5 h-5 text-red-600" />
                      <span className="text-xs text-center">T2 Corporate<br/>Income Tax</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2 border-red-200 hover:bg-red-50"
                      onClick={() => downloadPdfReport('financial-statements', `Financial_Statements_${fiscalYear}.pdf`)}
                      data-testid="download-financial-statements"
                    >
                      <FileText className="w-5 h-5 text-red-600" />
                      <span className="text-xs text-center">Year-End<br/>Financials</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2 border-red-200 hover:bg-red-50"
                      onClick={() => downloadPdfReport('schedule-8-cca', `Schedule_8_CCA_${fiscalYear}.pdf`)}
                      data-testid="download-schedule-8"
                    >
                      <Receipt className="w-5 h-5 text-red-600" />
                      <span className="text-xs text-center">Schedule 8<br/>(CCA)</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2 border-red-200 hover:bg-red-50"
                      onClick={() => downloadPdfReport('t4-t4a-summary', `T4_T4A_Summary_${fiscalYear}.pdf`)}
                      data-testid="download-t4-t4a-summary"
                    >
                      <Users className="w-5 h-5 text-red-600" />
                      <span className="text-xs text-center">T4/T4A<br/>Summary</span>
                    </Button>
                  </div>
                </div>

                {/* Sales Tax Reports */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Sales Tax Reports (GST/HST/PST)
                  </h4>
                  <div className="mb-3">
                    <Label className="text-sm text-slate-600">Select Quarter</Label>
                    <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                      <SelectTrigger className="w-48 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                        <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                        <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                        <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => downloadPdfReport(`gst-hst-return?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `GST_HST_Return_Q${selectedQuarter}_${fiscalYear}.pdf`)}
                      data-testid="download-gst-hst"
                    >
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                      <span className="text-xs text-center">GST/HST<br/>Return</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport(`pst-return/mb?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `RST_Manitoba_Q${selectedQuarter}_${fiscalYear}.pdf`)}
                      data-testid="download-pst-mb"
                    >
                      <span className="text-xs font-bold text-slate-600">MB</span>
                      <span className="text-xs text-center">RST</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport(`pst-return/bc?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `PST_BC_Q${selectedQuarter}_${fiscalYear}.pdf`)}
                      data-testid="download-pst-bc"
                    >
                      <span className="text-xs font-bold text-slate-600">BC</span>
                      <span className="text-xs text-center">PST</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport(`pst-return/sk?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `PST_SK_Q${selectedQuarter}_${fiscalYear}.pdf`)}
                      data-testid="download-pst-sk"
                    >
                      <span className="text-xs font-bold text-slate-600">SK</span>
                      <span className="text-xs text-center">PST</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport(`pst-return/on?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `HST_Ontario_Q${selectedQuarter}_${fiscalYear}.pdf`)}
                      data-testid="download-pst-on"
                    >
                      <span className="text-xs font-bold text-slate-600">ON</span>
                      <span className="text-xs text-center">HST</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="h-auto py-3 flex flex-col items-center gap-2"
                      onClick={() => downloadPdfReport(`pst-return/qc?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `QST_Quebec_Q${selectedQuarter}_${fiscalYear}.pdf`)}
                      data-testid="download-pst-qc"
                    >
                      <span className="text-xs font-bold text-slate-600">QC</span>
                      <span className="text-xs text-center">QST</span>
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Note: Additional provinces (NS, NB, NL, PE) available. Alberta, Yukon, NWT, and Nunavut do not have PST.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* T4/T4A Tab */}
        <TabsContent value="t4a" className="mt-6 space-y-6">
          {/* T4/T4A Summary Cards */}
          {t4aSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">T4 (Employees)</p>
                      <p className="text-xl font-bold text-blue-600">{taxSlipsData.summary?.total_t4 || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">T4A (Contractors)</p>
                      <p className="text-xl font-bold text-amber-600">{taxSlipsData.summary?.total_t4a || t4aSummary.summary?.total_t4a_slips || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Total Commissions</p>
                      <p className="text-xl font-bold font-mono text-emerald-600">
                        {formatCurrency((taxSlipsData.summary?.total_t4_amount || 0) + (taxSlipsData.summary?.total_t4a_amount || t4aSummary.summary?.total_box_020 || 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Filing Deadline</p>
                      <p className="text-lg font-bold text-red-600">{t4aSummary.summary?.filing_deadline}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <FileCheck className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Tax Year</p>
                      <p className="text-xl font-bold text-purple-600">{fiscalYear}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={downloadT4aSummary} data-testid="download-t4a-summary-btn">
              <Download className="w-4 h-4 mr-2" />
              Download T4/T4A Summary (CRA)
            </Button>
            <Button variant="outline" onClick={downloadAllT4aSlips} disabled={t4aAgents.length === 0}>
              <Printer className="w-4 h-4 mr-2" />
              Download All Slips ({t4aAgents.length})
            </Button>
          </div>

          {/* Division Breakdown */}
          {t4aSummary && (
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Mortgage Division
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">T4 Slips (Employees)</span>
                      <span className="font-bold">{t4aSummary.by_division?.mortgage?.t4_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">T4A Slips (Contractors)</span>
                      <span className="font-bold">{t4aSummary.by_division?.mortgage?.t4a_count || t4aSummary.by_division?.mortgage?.count || 0}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-slate-600 font-medium">Total Commissions</span>
                      <span className="font-mono font-bold text-emerald-600">{formatCurrency(t4aSummary.by_division?.mortgage?.total || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-purple-600" />
                    Insurance & Investment Division
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">T4 Slips (Employees)</span>
                      <span className="font-bold">{t4aSummary.by_division?.insurance?.t4_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">T4A Slips (Contractors)</span>
                      <span className="font-bold">{t4aSummary.by_division?.insurance?.t4a_count || t4aSummary.by_division?.insurance?.count || 0}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-slate-600 font-medium">Total Commissions</span>
                      <span className="font-mono font-bold text-emerald-600">{formatCurrency(t4aSummary.by_division?.insurance?.total || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* T4 - Employees Section */}
          <Card>
            <CardHeader className="bg-blue-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                T4 Slips - Employees
              </CardTitle>
              <CardDescription>
                T4 slips for employees with employment income, CPP/EI deductions, and tax withheld
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {(taxSlipsData.employees?.length || 0) === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No employees with commissions found for {fiscalYear}</p>
                  <p className="text-sm">Set agent employment type to &quot;Employee&quot; in Agents page to generate T4</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Agent Name</TableHead>
                        <TableHead>Division</TableHead>
                        <TableHead className="text-right">Box 14 (Employment Income)</TableHead>
                        <TableHead className="text-center">Payments</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taxSlipsData.employees?.map((agent, idx) => (
                        <TableRow key={agent.id}>
                          <TableCell className="font-mono text-slate-500">{idx + 1}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{agent.name}</p>
                              {agent.email && <p className="text-xs text-slate-500">{agent.email}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={agent.division === 'mortgage' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                              {agent.division}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-blue-600">
                            {formatCurrency(agent.total_commission)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{agent.commission_count}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => downloadT4Slip(agent.id, agent.name)}
                              className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                              data-testid={`download-t4-${agent.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              T4
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* T4A - Contractors Section */}
          <Card>
            <CardHeader className="bg-amber-50 border-b border-amber-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                T4A Slips - Contractors
              </CardTitle>
              <CardDescription>
                T4A slips for self-employed contractors with Box 020 (Commissions)
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {(taxSlipsData.contractors?.length || t4aAgents.filter(a => a.employment_type !== 'employee').length || 0) === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No contractors with commissions found for {fiscalYear}</p>
                  <p className="text-sm">Make sure commission data has payment dates within the selected fiscal year</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Agent Name</TableHead>
                        <TableHead>Division</TableHead>
                        <TableHead className="text-right">Box 020 (Commissions)</TableHead>
                        <TableHead className="text-center">Payments</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(taxSlipsData.contractors?.length > 0 ? taxSlipsData.contractors : t4aAgents.filter(a => a.employment_type !== 'employee')).map((agent, idx) => (
                        <TableRow key={agent.id}>
                          <TableCell className="font-mono text-slate-500">{idx + 1}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{agent.name}</p>
                              {agent.email && <p className="text-xs text-slate-500">{agent.email}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={agent.division === 'mortgage' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                              {agent.division}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-amber-600">
                            {formatCurrency(agent.total_commission)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{agent.commission_count}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => downloadT4aSlip(agent.id, agent.name)}
                              className="bg-amber-50 hover:bg-amber-100 text-amber-700"
                              data-testid={`download-t4a-${agent.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              T4A
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* CRA Filing Info */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-amber-800 mb-2">CRA Filing Information</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• <strong>T4A Slip:</strong> Statement of Pension, Retirement, Annuity, and Other Income</li>
                <li>• <strong>Box 020:</strong> Self-employed commissions paid to agents</li>
                <li>• <strong>Filing Deadline:</strong> Last day of February following the tax year</li>
                <li>• <strong>Note:</strong> T4A slips must be provided to recipients by the same deadline</li>
                <li>• <strong>Important:</strong> Verify all agent SIN numbers before official CRA submission</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Reconciliation Tab */}
        <TabsContent value="reconciliation" className="mt-6 space-y-6">
          {/* Summary Cards */}
          {reconciliationSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Unmatched</p>
                      <p className="text-xl font-bold text-amber-600">{reconciliationSummary.counts?.unmatched || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Matched</p>
                      <p className="text-xl font-bold text-emerald-600">{reconciliationSummary.counts?.matched || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Landmark className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Total</p>
                      <p className="text-xl font-bold text-blue-600">{reconciliationSummary.counts?.total || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <PieChart className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Match Rate</p>
                      <p className="text-xl font-bold text-purple-600">{reconciliationSummary.match_percentage || 0}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={() => setShowBankImportModal(true)} data-testid="import-bank-btn">
              <Upload className="w-4 h-4 mr-2" />
              Import Bank Statement
            </Button>
            <Button variant="outline" onClick={autoMatchTransactions} data-testid="auto-match-btn">
              <Zap className="w-4 h-4 mr-2" />
              Auto-Match Transactions
            </Button>
          </div>

          {/* Bank Transactions List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Landmark className="w-5 h-5" />
                Bank Transactions
              </CardTitle>
              <CardDescription>
                Import bank statements and match with accounting transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {bankTransactions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Landmark className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No bank transactions imported yet</p>
                  <p className="text-sm">Import your bank statement to start reconciliation</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankTransactions.map((trans) => (
                        <TableRow key={trans.id} className={trans.status === 'matched' ? 'bg-emerald-50' : ''}>
                          <TableCell className="font-mono text-sm">{trans.date}</TableCell>
                          <TableCell>{trans.description}</TableCell>
                          <TableCell className={`text-right font-mono ${trans.transaction_type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {trans.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(trans.amount)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={
                              trans.status === 'matched' ? 'bg-emerald-100 text-emerald-800' :
                              trans.status === 'reconciled' ? 'bg-blue-100 text-blue-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {trans.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {trans.status === 'matched' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => unmatchTransaction(trans.id)}
                                  title="Unmatch"
                                >
                                  <Unlink className="w-4 h-4 text-amber-600" />
                                </Button>
                              ) : (
                                <Select
                                  onValueChange={(accountingId) => matchTransaction(trans.id, accountingId)}
                                >
                                  <SelectTrigger className="w-8 h-8 p-0">
                                    <Link2 className="w-4 h-4 text-blue-600" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {transactions.filter(t => !t.reconciled).slice(0, 20).map(t => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.date} - {t.description?.substring(0, 20)} - {formatCurrency(t.debit || t.credit)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteBankTransaction(trans.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Bank Reconciliation Guide</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Import:</strong> Paste your bank statement data (Date, Description, Amount per line)</li>
                <li>• <strong>Auto-Match:</strong> Automatically matches transactions by amount and date (±3 days)</li>
                <li>• <strong>Manual Match:</strong> Click the link icon to manually match with accounting entries</li>
                <li>• <strong>Unmatched:</strong> Transactions that need review or manual entry in accounting</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chart of Accounts Tab */}
        <TabsContent value="accounts" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Chart of Accounts</CardTitle>
                <CardDescription>Manage your accounting categories. Click an account to view transactions.</CardDescription>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={recalculateBalances} data-testid="recalculate-balances-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Recalculate Balances
                  </Button>
                  <Button onClick={() => { resetChartAccountForm(); setShowChartAccountModal(true); }} data-testid="create-account-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Account
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account #</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chartOfAccounts.map(account => (
                      <TableRow 
                        key={account.id} 
                        className={`cursor-pointer hover:bg-slate-50 ${account.parent_account_id ? 'bg-slate-50' : ''}`}
                        onClick={() => handleAccountClick(account)}
                        data-testid={`account-row-${account.account_number}`}
                      >
                        <TableCell className="font-mono">
                          {account.parent_account_id && <span className="text-slate-400 mr-2">↳</span>}
                          {account.account_number}
                        </TableCell>
                        <TableCell className={account.parent_account_id ? 'pl-6' : ''}>
                          {account.name}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            account.account_type === 'asset' ? 'bg-blue-100 text-blue-800' :
                            account.account_type === 'liability' ? 'bg-red-100 text-red-800' :
                            account.account_type === 'equity' ? 'bg-purple-100 text-purple-800' :
                            account.account_type === 'income' ? 'bg-emerald-100 text-emerald-800' :
                            'bg-amber-100 text-amber-800'
                          }>
                            {account.account_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">
                          {account.description || '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(account.balance)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-emerald-100 text-emerald-700' : ''}>
                            {account.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }}
                                title="View Transactions"
                              >
                                <Eye className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => { e.stopPropagation(); handleCreateSubAccount(account); }}
                                title="Add Sub-Account"
                                data-testid={`add-subaccount-${account.id}`}
                              >
                                <Plus className="w-4 h-4 text-emerald-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => { e.stopPropagation(); handleEditChartAccount(account); }}
                                title="Edit Account"
                                data-testid={`edit-account-${account.id}`}
                              >
                                <Pencil className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => { e.stopPropagation(); deleteChartAccount(account); }}
                                title="Delete Account"
                                data-testid={`delete-account-${account.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Account Types Legend */}
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Account Types:</p>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="flex items-center gap-1"><Badge className="bg-blue-100 text-blue-800">Asset</Badge> What you own</span>
                  <span className="flex items-center gap-1"><Badge className="bg-red-100 text-red-800">Liability</Badge> What you owe</span>
                  <span className="flex items-center gap-1"><Badge className="bg-purple-100 text-purple-800">Equity</Badge> Owner's stake</span>
                  <span className="flex items-center gap-1"><Badge className="bg-emerald-100 text-emerald-800">Income</Badge> Revenue earned</span>
                  <span className="flex items-center gap-1"><Badge className="bg-amber-100 text-amber-800">Expense</Badge> Costs incurred</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Chart of Accounts Modal */}
      <Dialog open={showChartAccountModal} onOpenChange={(open) => {
        setShowChartAccountModal(open);
        if (!open) { resetChartAccountForm(); }
      }}>
        <DialogContent data-testid="chart-account-modal">
          <DialogHeader>
            <DialogTitle>
              {editingChartAccount ? 'Edit Account' : chartAccountForm.parent_account_id ? 'Create Sub-Account' : 'Create Account'}
            </DialogTitle>
            <DialogDescription>
              {chartAccountForm.parent_account_id 
                ? `Creating sub-account under ${chartOfAccounts.find(a => a.id === chartAccountForm.parent_account_id)?.name || 'parent'}`
                : editingChartAccount 
                  ? 'Update account details below'
                  : 'Add a new account to your chart of accounts'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input
                  value={chartAccountForm.account_number}
                  onChange={(e) => setChartAccountForm({ ...chartAccountForm, account_number: e.target.value })}
                  placeholder="e.g., 5100 or 5100.1"
                  data-testid="account-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Account Type *</Label>
                <Select
                  value={chartAccountForm.account_type}
                  onValueChange={(v) => setChartAccountForm({ ...chartAccountForm, account_type: v })}
                >
                  <SelectTrigger data-testid="account-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Account Name *</Label>
              <Input
                value={chartAccountForm.name}
                onChange={(e) => setChartAccountForm({ ...chartAccountForm, name: e.target.value })}
                placeholder="e.g., Advertising & Marketing"
                data-testid="account-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={chartAccountForm.description}
                onChange={(e) => setChartAccountForm({ ...chartAccountForm, description: e.target.value })}
                placeholder="Optional description for this account"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Parent Account (for sub-accounts)</Label>
              <Select
                value={chartAccountForm.parent_account_id || "none"}
                onValueChange={(v) => setChartAccountForm({ ...chartAccountForm, parent_account_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="parent-account-select">
                  <SelectValue placeholder="No parent (top-level account)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (top-level account)</SelectItem>
                  {chartOfAccounts.filter(a => !a.parent_account_id).map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_number} - {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={chartAccountForm.is_active}
                onChange={(e) => setChartAccountForm({ ...chartAccountForm, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Account is Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChartAccountModal(false)}>Cancel</Button>
            <Button onClick={saveChartAccount} data-testid="save-account-btn">
              {editingChartAccount ? 'Update Account' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Modal */}
      <Dialog open={showTransactionModal} onOpenChange={(open) => { 
        setShowTransactionModal(open); 
        if (!open) { setEditingTransactionId(null); resetTransactionForm(); }
      }}>
        <DialogContent data-testid="transaction-modal">
          <DialogHeader>
            <DialogTitle>{editingTransactionId ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Account *</Label>
                <Select
                  value={transactionForm.account_id}
                  onValueChange={(v) => setTransactionForm({ ...transactionForm, account_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {chartOfAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.account_number} - {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={transactionForm.description}
                onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                placeholder="Transaction description"
              />
            </div>

            <div className="space-y-2">
              <Label>Vendor</Label>
              <Input
                value={transactionForm.vendor}
                onChange={(e) => setTransactionForm({ ...transactionForm, vendor: e.target.value })}
                placeholder="e.g., Supplier name, Payee"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Debit (Income)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={transactionForm.debit}
                  onChange={(e) => setTransactionForm({ ...transactionForm, debit: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Credit (Expense)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={transactionForm.credit}
                  onChange={(e) => setTransactionForm({ ...transactionForm, credit: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={transactionForm.category}
                  onChange={(e) => setTransactionForm({ ...transactionForm, category: e.target.value })}
                  placeholder="e.g., Office Supplies"
                />
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input
                  value={transactionForm.reference}
                  onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })}
                  placeholder="e.g., Invoice #, Check #"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTransactionModal(false); setEditingTransactionId(null); resetTransactionForm(); }}>Cancel</Button>
            <Button onClick={editingTransactionId ? updateTransaction : createTransaction}>
              {editingTransactionId ? 'Update Transaction' : 'Create Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              {deleteTarget === 'bulk' 
                ? `Are you sure you want to delete ${selectedTransactionIds.size} selected transaction(s)? This action cannot be undone.`
                : 'Are you sure you want to delete this transaction? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteTarget === 'bulk' ? `Delete ${selectedTransactionIds.size} Transaction(s)` : 'Delete Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={(open) => {
        setShowInvoiceModal(open);
        if (!open) { setEditingInvoice(null); resetInvoiceForm(); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-modal">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Agent Selector for Quick Fill */}
            <div className="space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <Label className="text-blue-700">Quick Fill from Agent</Label>
              <Select onValueChange={handleAgentSelectForInvoice}>
                <SelectTrigger data-testid="agent-select-for-invoice">
                  <SelectValue placeholder="Select an agent to populate client info..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">-- Manual Entry --</SelectItem>
                  {allAgents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} {agent.email ? `(${agent.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-600">Select an agent to auto-fill client info, or enter manually below</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={invoiceForm.client_name}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, client_name: e.target.value })}
                  placeholder="Enter client name or select agent above"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={invoiceForm.client_email}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, client_email: e.target.value })}
                  placeholder="client@example.com"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Client Address</Label>
              <Textarea
                value={invoiceForm.client_address}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, client_address: e.target.value })}
                rows={2}
                placeholder="Enter client address"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Invoice Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceForm.tax_rate}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            {/* Accounting Category */}
            <div className="space-y-2">
              <Label>Accounting Category</Label>
              <Select
                value={invoiceForm.accounting_account_id || "none"}
                onValueChange={(v) => setInvoiceForm({ ...invoiceForm, accounting_account_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="invoice-category-select">
                  <SelectValue placeholder="Select accounting category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {chartOfAccounts.filter(a => a.account_type === 'income').map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_number} - {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Link this invoice to an income account</p>
            </div>
            
            {/* Line Items */}
            <div className="space-y-2">
              <Label>Line Items</Label>
              {invoiceForm.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    placeholder="Description"
                    className="flex-1"
                    value={item.description}
                    onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Qty"
                    className="w-20"
                    value={item.quantity}
                    onChange={(e) => updateInvoiceItem(idx, 'quantity', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Price"
                    className="w-28"
                    value={item.unit_price}
                    onChange={(e) => updateInvoiceItem(idx, 'unit_price', e.target.value)}
                  />
                  <span className="font-mono w-24 text-right">{formatCurrency(item.quantity * item.unit_price)}</span>
                  <Button variant="ghost" size="sm" onClick={() => removeInvoiceItem(idx)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addInvoiceItem}>
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            {/* Totals */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-mono">{formatCurrency(calculateInvoiceTotal().subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax ({invoiceForm.tax_rate}%)</span>
                <span className="font-mono">{formatCurrency(calculateInvoiceTotal().tax)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className="font-mono">{formatCurrency(calculateInvoiceTotal().total)}</span>
              </div>
            </div>

            {/* Recurring Invoice Options */}
            <div className="border rounded-lg p-4 space-y-3 bg-blue-50/50">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_recurring"
                  checked={invoiceForm.is_recurring}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, is_recurring: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="is_recurring" className="cursor-pointer">
                  Make this a recurring invoice template
                </Label>
              </div>
              {invoiceForm.is_recurring && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select 
                      value={invoiceForm.recurring_frequency} 
                      onValueChange={(v) => setInvoiceForm({ ...invoiceForm, recurring_frequency: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={invoiceForm.recurring_end_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, recurring_end_date: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                placeholder="Additional notes for the invoice..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
            <Button onClick={createInvoice}>Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Recording Modal - Using Extracted Component */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        invoice={selectedInvoiceForPayment}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        onRecordPayment={recordPayment}
      />

      {/* Aging Report Modal - Using Extracted Component */}
      <AgingReportModal
        isOpen={showAgingReport}
        onClose={() => setShowAgingReport(false)}
        agingReportData={agingReportData}
        onDownloadPdf={downloadAgingReportPdf}
      />

      {/* Bank Import Modal */}
      <Dialog open={showBankImportModal} onOpenChange={setShowBankImportModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5" />
              Import Bank Statement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Paste Bank Data (CSV format)</Label>
              <p className="text-xs text-slate-500">Format: Date, Description, Amount (one per line)</p>
              <textarea
                className="w-full h-48 p-3 border rounded-lg font-mono text-sm"
                placeholder="2026-01-15, Deposit from Client A, 1500.00
2026-01-16, Office Supplies, -125.50
2026-01-18, Commission Payment, -500.00"
                value={bankImportData}
                onChange={(e) => setBankImportData(e.target.value)}
              />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Tip:</strong> Export your bank statement as CSV and paste here. 
                Positive amounts = deposits (credits), Negative amounts = withdrawals (debits).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankImportModal(false)}>Cancel</Button>
            <Button onClick={importBankTransactions}>
              <Upload className="w-4 h-4 mr-2" />
              Import Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income Details Modal */}
      <Dialog open={showIncomeDetailsModal} onOpenChange={setShowIncomeDetailsModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Income Details - {fiscalYear}
            </DialogTitle>
            <DialogDescription>
              Breakdown of all income sources. Click any row to view the source transaction.
            </DialogDescription>
          </DialogHeader>

          {incomeDetails && (
            <div className="flex-1 overflow-auto space-y-6 py-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-xs text-emerald-600 uppercase">Gross Commission</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">{formatCurrency(incomeDetails.totals.gross_commission)}</p>
                  <p className="text-xs text-emerald-600 mt-1">{incomeDetails.commissions.length} transactions</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-600 uppercase">Chart of Accounts</p>
                  <p className="text-xl font-bold font-mono text-blue-700">{formatCurrency(incomeDetails.totals.chart_of_accounts)}</p>
                  <p className="text-xs text-blue-600 mt-1">{incomeDetails.chart_of_accounts.length} transactions</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-4">
                  <p className="text-xs text-slate-600 uppercase">Total Income</p>
                  <p className="text-xl font-bold font-mono text-slate-900">{formatCurrency(incomeDetails.totals.total)}</p>
                </div>
              </div>

              {/* Commission Details - ALL records */}
              {incomeDetails.commissions.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Gross Commissions ({incomeDetails.commissions.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '400px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Statement #</th>
                          <th className="text-left p-2 font-medium">Agent</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Lender</th>
                          <th className="text-left p-2 font-medium">Division</th>
                          <th className="text-right p-2 font-medium">Gross Commission</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {incomeDetails.commissions.map((comm, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-emerald-50 cursor-pointer transition-colors"
                            onClick={() => navigateToCommission(comm)}
                          >
                            <td className="p-2">{comm.date}</td>
                            <td className="p-2 font-mono text-xs">{comm.statement_no}</td>
                            <td className="p-2">{comm.agent_name}</td>
                            <td className="p-2 max-w-xs truncate" title={comm.description}>{comm.description}</td>
                            <td className="p-2">{comm.lender}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={comm.division === 'insurance' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}>
                                {comm.division}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono text-emerald-600 font-medium">{formatCurrency(comm.gross_commission)}</td>
                            <td className="p-2 text-center">
                              <ExternalLink className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-emerald-100 sticky bottom-0">
                        <tr>
                          <td colSpan={6} className="p-2 font-semibold text-right">Total Gross Commission:</td>
                          <td className="p-2 text-right font-mono font-bold text-emerald-700">{formatCurrency(incomeDetails.totals.gross_commission)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Chart of Accounts Income - ALL records */}
              {incomeDetails.chart_of_accounts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Chart of Accounts Income ({incomeDetails.chart_of_accounts.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '300px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Account</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Reference</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {incomeDetails.chart_of_accounts.map((trans, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-blue-50 cursor-pointer transition-colors"
                            onClick={() => navigateToTransaction(trans)}
                          >
                            <td className="p-2">{trans.date}</td>
                            <td className="p-2">{trans.account_name}</td>
                            <td className="p-2 max-w-xs truncate" title={trans.description}>{trans.description}</td>
                            <td className="p-2">{trans.reference}</td>
                            <td className="p-2 text-right font-mono text-blue-600 font-medium">{formatCurrency(trans.amount)}</td>
                            <td className="p-2 text-center">
                              <ExternalLink className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-blue-100 sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="p-2 font-semibold text-right">Total Chart of Accounts:</td>
                          <td className="p-2 text-right font-mono font-bold text-blue-700">{formatCurrency(incomeDetails.totals.chart_of_accounts)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomeDetailsModal(false)}>Close</Button>
            {expenseDetails && (
              <Button onClick={() => { setShowIncomeDetailsModal(false); setShowExpenseDetailsModal(true); }}>
                View Expenses →
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Details Modal */}
      <Dialog open={showExpenseDetailsModal} onOpenChange={setShowExpenseDetailsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Expense Details - {fiscalYear}
            </DialogTitle>
            <DialogDescription>
              Breakdown of all expense sources. Click any row to view the source transaction.
            </DialogDescription>
          </DialogHeader>

          {expenseDetails && (
            <div className="flex-1 overflow-auto space-y-6 py-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs text-orange-600 uppercase">Agent Commission</p>
                  <p className="text-lg font-bold font-mono text-orange-700">{formatCurrency(expenseDetails.totals.agent_commissions)}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs text-amber-600 uppercase">Co-Agent Comm.</p>
                  <p className="text-lg font-bold font-mono text-amber-700">{formatCurrency(expenseDetails.totals.co_agent_commissions)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 uppercase">Royalty + GST</p>
                  <p className="text-lg font-bold font-mono text-purple-700">{formatCurrency(expenseDetails.totals.royalty_gst)}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-xs text-red-600 uppercase">Expense Records</p>
                  <p className="text-lg font-bold font-mono text-red-700">{formatCurrency(expenseDetails.totals.expense_records)}</p>
                  <p className="text-xs text-red-500 mt-1">{expenseDetails.expense_records.length} records</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-xs text-slate-600 uppercase">Total Expenses</p>
                  <p className="text-lg font-bold font-mono text-slate-900">{formatCurrency(expenseDetails.totals.total)}</p>
                </div>
              </div>

              {/* Commission Payouts - ALL records */}
              {expenseDetails.commission_payouts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Commission Payouts ({expenseDetails.commission_payouts.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '350px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Agent</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Division</th>
                          <th className="text-right p-2 font-medium">Agent Comm.</th>
                          <th className="text-right p-2 font-medium">Co-Agent</th>
                          <th className="text-right p-2 font-medium">Royalty+GST</th>
                          <th className="text-right p-2 font-medium">Total</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseDetails.commission_payouts.map((payout, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-orange-50 cursor-pointer transition-colors"
                            onClick={() => navigateToCommission(payout)}
                          >
                            <td className="p-2">{payout.date}</td>
                            <td className="p-2">{payout.agent_name}</td>
                            <td className="p-2 max-w-xs truncate" title={payout.description}>{payout.description}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={payout.division === 'insurance' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}>
                                {payout.division}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono text-orange-600">{formatCurrency(payout.agent_commission)}</td>
                            <td className="p-2 text-right font-mono text-amber-600">{formatCurrency(payout.co_agent_commission)}</td>
                            <td className="p-2 text-right font-mono text-purple-600">{formatCurrency(payout.royalty_gst)}</td>
                            <td className="p-2 text-right font-mono font-medium">{formatCurrency(payout.total)}</td>
                            <td className="p-2 text-center">
                              <ExternalLink className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-orange-100 sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="p-2 font-semibold text-right">Totals:</td>
                          <td className="p-2 text-right font-mono font-bold text-orange-700">{formatCurrency(expenseDetails.totals.agent_commissions)}</td>
                          <td className="p-2 text-right font-mono font-bold text-amber-700">{formatCurrency(expenseDetails.totals.co_agent_commissions)}</td>
                          <td className="p-2 text-right font-mono font-bold text-purple-700">{formatCurrency(expenseDetails.totals.royalty_gst)}</td>
                          <td className="p-2 text-right font-mono font-bold">{formatCurrency(expenseDetails.totals.agent_commissions + expenseDetails.totals.co_agent_commissions + expenseDetails.totals.royalty_gst)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Expense Records - ALL records */}
              {expenseDetails.expense_records.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4" /> Expense Records ({expenseDetails.expense_records.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '300px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Agent</th>
                          <th className="text-left p-2 font-medium">Category</th>
                          <th className="text-left p-2 font-medium">Vendor</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Division</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseDetails.expense_records.map((exp, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-red-50 cursor-pointer transition-colors"
                            onClick={() => navigateToExpense(exp)}
                          >
                            <td className="p-2">{exp.date}</td>
                            <td className="p-2">{exp.agent_name}</td>
                            <td className="p-2">{exp.category}</td>
                            <td className="p-2">{exp.vendor}</td>
                            <td className="p-2 max-w-xs truncate" title={exp.description}>{exp.description}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={exp.division === 'insurance' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}>
                                {exp.division}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono text-red-600 font-medium">{formatCurrency(exp.amount)}</td>
                            <td className="p-2 text-center">
                              <ExternalLink className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-red-100 sticky bottom-0">
                        <tr>
                          <td colSpan={6} className="p-2 font-semibold text-right">Total Expense Records:</td>
                          <td className="p-2 text-right font-mono font-bold text-red-700">{formatCurrency(expenseDetails.totals.expense_records)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Chart of Accounts Expenses - ALL records */}
              {expenseDetails.chart_of_accounts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Chart of Accounts Expenses ({expenseDetails.chart_of_accounts.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '250px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Account</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Reference</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseDetails.chart_of_accounts.map((trans, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-slate-100 cursor-pointer transition-colors"
                            onClick={() => navigateToTransaction(trans)}
                          >
                            <td className="p-2">{trans.date}</td>
                            <td className="p-2">{trans.account_name}</td>
                            <td className="p-2 max-w-xs truncate" title={trans.description}>{trans.description}</td>
                            <td className="p-2">{trans.reference}</td>
                            <td className="p-2 text-right font-mono text-red-600 font-medium">{formatCurrency(trans.amount)}</td>
                            <td className="p-2 text-center">
                              <ExternalLink className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-200 sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="p-2 font-semibold text-right">Total Chart of Accounts:</td>
                          <td className="p-2 text-right font-mono font-bold">{formatCurrency(expenseDetails.totals.chart_of_accounts)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {incomeDetails && (
              <Button variant="outline" onClick={() => { setShowExpenseDetailsModal(false); setShowIncomeDetailsModal(true); }}>
                ← View Income
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowExpenseDetailsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Transactions Modal */}
      <Dialog open={showAccountTransactionsModal} onOpenChange={setShowAccountTransactionsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="account-transactions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              {selectedAccount?.account_number} - {selectedAccount?.name}
            </DialogTitle>
            <DialogDescription>
              All transactions for this account in {fiscalYear}
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Account Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-600 uppercase">Total Transactions</p>
                  <p className="text-xl font-bold text-blue-700">{accountTransactions.length}</p>
                </div>
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase">Total Debits</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">
                    {formatCurrency(accountTransactions.reduce((sum, t) => sum + (t.debit || 0), 0))}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs text-red-600 uppercase">Total Credits</p>
                  <p className="text-xl font-bold font-mono text-red-700">
                    {formatCurrency(accountTransactions.reduce((sum, t) => sum + (t.credit || 0), 0))}
                  </p>
                </div>
              </div>

              {/* Transactions Table */}
              {accountTransactions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No transactions found for this account in {fiscalYear}
                </div>
              ) : (
                <div className="border rounded-lg overflow-auto" style={{ maxHeight: '400px' }}>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-2 font-medium">Date</th>
                        <th className="text-left p-2 font-medium">Description</th>
                        <th className="text-left p-2 font-medium">Category</th>
                        <th className="text-left p-2 font-medium">Vendor</th>
                        <th className="text-left p-2 font-medium">Reference</th>
                        <th className="text-right p-2 font-medium">Debit</th>
                        <th className="text-right p-2 font-medium">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {accountTransactions.map((trans, idx) => (
                        <tr 
                          key={idx} 
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setShowAccountTransactionsModal(false);
                            // Navigate based on source
                            if (trans.source === 'expense') {
                              window.location.href = '/expenses';
                            } else if (trans.source === 'commission') {
                              window.location.href = '/commissions';
                            } else {
                              // Stay on accounting transactions tab
                              setActiveTab('transactions');
                            }
                          }}
                        >
                          <td className="p-2">{formatDate(trans.date)}</td>
                          <td className="p-2 max-w-xs truncate" title={trans.description}>{trans.description}</td>
                          <td className="p-2">{trans.category || '—'}</td>
                          <td className="p-2">{trans.vendor || '—'}</td>
                          <td className="p-2 font-mono text-xs">{trans.reference || '—'}</td>
                          <td className="p-2 text-right font-mono text-emerald-600">
                            {trans.debit > 0 ? formatCurrency(trans.debit) : '—'}
                          </td>
                          <td className="p-2 text-right font-mono text-red-600">
                            {trans.credit > 0 ? formatCurrency(trans.credit) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 sticky bottom-0">
                      <tr>
                        <td colSpan={5} className="p-2 font-semibold text-right">Net Balance:</td>
                        <td colSpan={2} className="p-2 text-right font-mono font-bold">
                          {formatCurrency(selectedAccount?.balance || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountTransactionsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
